import React, { useState, useEffect } from 'react';
import './trailOrder.css';
import Swal from 'sweetalert2';
import { FaFilter, FaCalendarAlt, FaBoxOpen, FaTimes, FaCheck, FaSpinner } from 'react-icons/fa';

const TrialOrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, totalOrders: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ trialStatus: '', trialPlacedDate: '', trialExpectedDate: '' });
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("admintoken");
      try {
        const response = await fetch(
          `https://admin.riymay.com/api/v1/admin/trail/order/management?page=${pagination.page}&limit=${pagination.limit}`,
          {
            method: 'POST',
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(filters),
          }
        );
        if (!response.ok) throw new Error('Error fetching orders');
        const data = await response.json();
        if (data.success) {
          setOrders(data.data);
          setPagination({ page: data.page, limit: data.limit, totalOrders: data.totalOrders, totalPages: data.totalPages });
        } else {
          throw new Error('Failed to fetch orders');
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [pagination.page, filters]);

  const cancelOrder = async (trailOrderId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You are about to cancel this trial order",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, cancel it!',
      cancelButtonText: 'No, keep it'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setActionLoading(trailOrderId);
          const token = localStorage.getItem("admintoken");
          
          const request = await fetch(
            `https://admin.riymay.com/api/v1/admin/trail/order/management/update/order/status/${trailOrderId}`, 
            {
              method: 'PUT',
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({"trialStatus":"cancelled"}),
            }
          );
          
          const response = await request.json();
          
          if(response.success) {
            Swal.fire({
              title: 'Cancelled!',
              text: 'Trial order has been cancelled successfully.',
              icon: 'success',
              confirmButtonColor: '#3085d6'
            });
            
            // Update the order status in the UI
            setOrders(orders.map(order => 
              order._id === trailOrderId 
                ? {...order, trialStatus: 'cancelled'} 
                : order
            ));
          } else {
            throw new Error(response.message || 'Failed to cancel order');
          }
        } catch (error) {
          Swal.fire({
            title: 'Error!',
            text: error.message || 'Failed to cancel the order. Please try again.',
            icon: 'error',
            confirmButtonColor: '#3085d6'
          });
        } finally {
          setActionLoading(null);
        }
      }
    });
  };

  const handlePageChange = (newPage) => {
    setPagination((prevState) => ({ ...prevState, page: newPage }));
    window.scrollTo(0, 0);
  };

  const handleFilterChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });
  
  const clearFilters = () => {
    setFilters({ trialStatus: '', trialPlacedDate: '', trialExpectedDate: '' });
  };

  const toggleOrderExpand = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const getStatusClass = (status) => {
    switch(status.toLowerCase()) {
      case 'completed': return 'status-completed';
      case 'confirmed': return 'status-confirmed';
      case 'failed': return 'status-failed';
      case 'cancelled': return 'status-cancelled';
      default: return '';
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="trial-order-management">
      <h1 className="trial-order-title">
        <FaBoxOpen className="title-icon" /> Trial Order Management
      </h1>

      <div className="filters-section">
        <div className="filters-header">
          <h2><FaFilter /> Filters</h2>
          {(filters.trialStatus || filters.trialPlacedDate || filters.trialExpectedDate) && (
            <button className="clear-filters-btn" onClick={clearFilters}>
              <FaTimes /> Clear Filters
            </button>
          )}
        </div>

        <div className="filters-container">
          {['completed', 'confirmed', 'failed', 'cancelled'].map((status) => (
            <button
              key={status}
              onClick={() => setFilters({ ...filters, trialStatus: filters.trialStatus === status ? '' : status })}
              className={`filter-button ${filters.trialStatus === status ? 'filter-button-active' : ''}`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        <div className="date-filters-container">
          <div className="date-filter-group">
            <label className="date-filter-label">
              <FaCalendarAlt className="date-icon" /> Placed Date:
              <input
                type="date"
                name="trialPlacedDate"
                value={filters.trialPlacedDate}
                onChange={handleFilterChange}
                className="date-filter-input"
              />
            </label>
          </div>

          <div className="date-filter-group">
            <label className="date-filter-label">
              <FaCalendarAlt className="date-icon" /> Expected Date:
              <input
                type="date"
                name="trialExpectedDate"
                value={filters.trialExpectedDate}
                onChange={handleFilterChange}
                className="date-filter-input"
              />
            </label>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading orders...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <FaTimes className="error-icon" />
          <p className="error-text">{error}</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            Retry
          </button>
        </div>
      ) : orders.length === 0 ? (
        <div className="no-orders-container">
          <p className="no-orders-text">No trial orders found matching your criteria.</p>
        </div>
      ) : (
        <div className="orders-list">
          {orders.map((order) => (
            <div key={order._id} className={`order-card ${expandedOrder === order._id ? 'order-card-expanded' : ''}`}>
              <div className="order-header" onClick={() => toggleOrderExpand(order._id)}>
                <div className="order-header-left">
                  <h3 className="order-id">Order #{order._id.substring(order._id.length - 8)}</h3>
                  <span className={`order-status ${getStatusClass(order.trialStatus)}`}>
                    {order.trialStatus}
                  </span>
                </div>
                <div className="order-header-right">
                  <div className="order-dates">
                    <span className="order-date">
                      <strong>Placed:</strong> {formatDate(order.trialPlacedDate)}
                    </span>
                    <span className="order-date">
                      <strong>Expected:</strong> {formatDate(order.trialExpectedDate)}
                    </span>
                  </div>
                  <div className="expand-indicator">
                    {expandedOrder === order._id ? '▲' : '▼'}
                  </div>
                </div>
              </div>

              {expandedOrder === order._id && (
                <div className="order-details">
                  <div className="order-info">
                    <div className="info-group">
                      <h4>Order Details</h4>
                      <p><strong>Order ID:</strong> {order._id}</p>
                      <p><strong>User ID:</strong> {order.userId}</p>
                      <p><strong>Status:</strong> {order.trialStatus}</p>
                    </div>
                    
                    <div className="info-group">
                      <h4>Dates</h4>
                      <p><strong>Placed Date:</strong> {formatDate(order.trialPlacedDate)}</p>
                      <p><strong>Expected Date:</strong> {formatDate(order.trialExpectedDate)}</p>
                    </div>
                    
                    <div className="info-group">
                      <h4>Shipping Address</h4>
                      <p>{order.trialShippingAddress.street}</p>
                      <p>{order.trialShippingAddress.city}, {order.trialShippingAddress.state} {order.trialShippingAddress.pinCode}</p>
                      <p>{order.trialShippingAddress.country}</p>
                    </div>
                  </div>

                  <div className="order-items-container">
                    <h4 className="items-title">Order Items ({order.trialItems.length})</h4>
                    <div className="order-items-grid">
                      {order.trialItems.map((item) => (
                        <div key={item._id} className="order-item-card">
                          <div className="item-image-container">
                            <img 
                              src={`https://riymay.com/assets/${item.productUrl}`} 
                              alt={item.productName} 
                              className="order-item-img"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgZmlsbD0iI2YxZjFmMSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSIjODg4ODg4Ij5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=";
                              }}
                            />
                          </div>
                          <div className="item-details">
                            <h5 className="item-name">{item.productName}</h5>
                            <div className="item-specs">
                              <p><span>Price:</span> ₹{item.unitPrice.toLocaleString()}</p>
                              <p><span>Qty:</span> {item.quantity}</p>
                              <p><span>Total:</span> ₹{item.totalPrice.toLocaleString()}</p>
                              <div className="item-attributes">
                                {item.color && <p className="item-color"><span>Color:</span> {item.color}</p>}
                                {item.size && <p className="item-size"><span>Size:</span> {item.size}</p>}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {!(order.trialStatus === "cancelled" || order.trialStatus === "completed") && (
                    <div className="order-actions">
                      <button 
                        className="cancel-order-btn" 
                        onClick={() => cancelOrder(order._id)}
                        disabled={actionLoading === order._id}
                      >
                        {actionLoading === order._id ? (
                          <><FaSpinner className="spinner-icon" /> Processing...</>
                        ) : (
                          <><FaTimes /> Cancel Order</>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && orders.length > 0 && (
        <div className="pagination-container">
          <button
            disabled={pagination.page <= 1}
            onClick={() => handlePageChange(pagination.page - 1)}
            className={`pagination-button ${pagination.page <= 1 ? 'pagination-button-disabled' : ''}`}
          >
            ← Previous
          </button>

          <span className="pagination-text">
            Page {pagination.page} of {pagination.totalPages || 1}
            {pagination.totalOrders > 0 && ` (${pagination.totalOrders} orders)`}
          </span>

          <button
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => handlePageChange(pagination.page + 1)}
            className={`pagination-button ${pagination.page >= pagination.totalPages ? 'pagination-button-disabled' : ''}`}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default TrialOrderManagement;